import React, { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, CalendarDays, Check, Copy, Image, List, LoaderCircle, Lock, LogOut, RefreshCw, Star, StickyNote } from "lucide-react";
import { BrandMark } from "./BrandMark";
import TeamCalendar from "./TeamCalendar";
import TeamTestimonials from "./TeamTestimonials";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = ["new", "replied", "booked"];
const STATUS_LABELS = { new: "New", replied: "Replied", booked: "Booked" };

function DepositControl({ inquiry, code, onUpdated }) {
  const suggested = (inquiry.estimated_total * 0.5).toFixed(2);
  const [amount, setAmount] = useState(inquiry.deposit_amount ? String(inquiry.deposit_amount) : suggested);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (inquiry.deposit_amount) setAmount(String(inquiry.deposit_amount));
  }, [inquiry.deposit_amount]);
  const amountInvalid = amount !== "" && !(Number(amount) > 0);
  const paymentUrl = `${process.env.REACT_APP_BACKEND_URL}/pay/${inquiry.id}`;

  const setDeposit = async () => {
    if (amountInvalid) return;
    setBusy(true);
    try {
      const response = await fetch(`${API}/team/inquiries/${inquiry.id}/deposit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Team-Code": code },
        body: JSON.stringify({ amount: Number(amount) || null }),
      });
      if (response.ok) onUpdated();
    } finally {
      setBusy(false);
    }
  };
  const copyLink = async () => {
    await navigator.clipboard.writeText(paymentUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  if (inquiry.deposit_status === "paid") {
    return <div className="deposit-paid" data-testid={`deposit-paid-${inquiry.id}`}><Check size={14} /> Deposit paid — ${Number(inquiry.deposit_amount).toLocaleString()}</div>;
  }
  return (
    <div className="deposit-control">
      <div className="deposit-row">
        <span className="deposit-currency">$</span>
        <input className="input-control deposit-input" type="number" min="1" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} data-testid={`deposit-amount-input-${inquiry.id}`} />
        <button type="button" className="button button-primary deposit-btn" onClick={setDeposit} disabled={busy || amountInvalid} data-testid={`deposit-set-button-${inquiry.id}`}>{busy ? <LoaderCircle className="spin" size={14} /> : null} {inquiry.deposit_amount ? "Update deposit" : "Set deposit"}</button>
      </div>
      {amountInvalid && <span className="team-gate-error" data-testid={`deposit-amount-error-${inquiry.id}`}>Enter an amount above $0 (or clear the field to use the 50% default).</span>}
      <span className="deposit-hint">Suggested: ${Number(suggested).toLocaleString()} (50% of quote)</span>
      {inquiry.deposit_status === "awaiting_payment" && (
        <button type="button" className="text-button" onClick={copyLink} data-testid={`copy-payment-link-${inquiry.id}`}>{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Link copied" : "Copy payment link"}</button>
      )}
      {inquiry.reminder_sent_at && <span className="deposit-hint" data-testid={`reminder-sent-${inquiry.id}`}>Reminder emailed {inquiry.reminder_sent_at.slice(0, 10)}</span>}
      {inquiry.review_request_sent_at && <span className="deposit-hint" data-testid={`review-requested-${inquiry.id}`}>Review request emailed {inquiry.review_request_sent_at.slice(0, 10)}</span>}
    </div>
  );
}

function TeamNotes({ inquiry, code, onUpdated }) {
  const [notes, setNotes] = useState(inquiry.team_notes || "");
  const [state, setState] = useState("idle");
  const dirty = notes !== (inquiry.team_notes || "");
  const save = async () => {
    setState("saving");
    const response = await fetch(`${API}/team/inquiries/${inquiry.id}/notes`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-Team-Code": code },
      body: JSON.stringify({ team_notes: notes }),
    });
    setState(response.ok ? "saved" : "idle");
    if (response.ok) {
      onUpdated();
      window.setTimeout(() => setState("idle"), 1800);
    }
  };
  return (
    <div className="team-notes">
      <span className="team-notes-label"><StickyNote size={13} /> Private team notes</span>
      <textarea className="input-control textarea-control" rows="2" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Follow-up reminders, tasting date, staffing thoughts… (guests never see this)" data-testid={`team-notes-input-${inquiry.id}`} />
      {(dirty || state !== "idle") && (
        <button type="button" className="text-button" onClick={save} disabled={state === "saving" || !dirty} data-testid={`team-notes-save-${inquiry.id}`}>
          {state === "saving" ? <LoaderCircle className="spin" size={13} /> : <Check size={13} />} {state === "saving" ? "Saving…" : state === "saved" ? "Saved" : "Save note"}
        </button>
      )}
    </div>
  );
}

function InquiryCard({ inquiry, code, onUpdated }) {
  const setStatus = async (status) => {
    await fetch(`${API}/team/inquiries/${inquiry.id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-Team-Code": code },
      body: JSON.stringify({ status }),
    });
    onUpdated();
  };
  return (
    <article className={`inquiry-card status-${inquiry.status}`} data-testid={`inquiry-card-${inquiry.id}`}>
      <div className="inquiry-head">
        <div>
          <h3 data-testid={`inquiry-name-${inquiry.id}`}>{inquiry.full_name}</h3>
          <span className="inquiry-meta">{inquiry.event_date} · {inquiry.guest_count} guests · {inquiry.event_type}</span>
        </div>
        <div className="status-switch">
          {STATUS_OPTIONS.map((option) => (
            <button type="button" key={option} className={`status-chip${inquiry.status === option ? " is-active" : ""}`} onClick={() => setStatus(option)} data-testid={`status-${option}-${inquiry.id}`}>{STATUS_LABELS[option]}</button>
          ))}
        </div>
      </div>
      <dl className="inquiry-details">
        <div><dt>Contact</dt><dd><a href={`mailto:${inquiry.email}`}>{inquiry.email}</a> · {inquiry.phone}</dd></div>
        <div><dt>Venue</dt><dd>{inquiry.event_location}</dd></div>
        <div><dt>Service</dt><dd>{inquiry.service_style} · {inquiry.duration_hours}h · est. ${Number(inquiry.estimated_total).toLocaleString()}</dd></div>
        {inquiry.selected_cocktails?.length > 0 && <div><dt>Cocktails</dt><dd>{inquiry.selected_cocktails.join(", ")}</dd></div>}
        {inquiry.charcuterie_presentation && <div><dt>Charcuterie</dt><dd>{inquiry.charcuterie_presentation}{inquiry.dietary_allergens?.length ? ` · ${inquiry.dietary_allergens.join(", ")}` : ""}</dd></div>}
        {inquiry.dietary_notes && <div><dt>Dietary</dt><dd>{inquiry.dietary_notes}</dd></div>}
        {inquiry.notes && <div><dt>Notes</dt><dd>{inquiry.notes}</dd></div>}
      </dl>
      <TeamNotes inquiry={inquiry} code={code} onUpdated={onUpdated} />
      <div className="inquiry-foot">
        <DepositControl inquiry={inquiry} code={code} onUpdated={onUpdated} />
        {inquiry.has_menu_card && <a className="text-button" href={`${API}/menu-cards/${inquiry.id}`} target="_blank" rel="noreferrer" data-testid={`menu-card-link-${inquiry.id}`}><Image size={14} /> Guest menu card <ArrowUpRight size={13} /></a>}
      </div>
    </article>
  );
}

export default function TeamDashboard() {
  const [code, setCode] = useState(() => window.sessionStorage.getItem("popTeamCode") || "");
  const [entry, setEntry] = useState("");
  const [authError, setAuthError] = useState("");
  const [inquiries, setInquiries] = useState(null);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list");

  const load = useCallback(async (activeCode) => {
    const response = await fetch(`${API}/team/inquiries`, { headers: { "X-Team-Code": activeCode } });
    if (response.status === 401) {
      window.sessionStorage.removeItem("popTeamCode");
      setCode("");
      return;
    }
    const payload = await response.json();
    setInquiries(payload.inquiries || []);
  }, []);

  useEffect(() => {
    if (code) load(code);
  }, [code, load]);

  const login = async (event) => {
    event.preventDefault();
    setAuthError("");
    const response = await fetch(`${API}/team/verify`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ passcode: entry }) });
    if (!response.ok) {
      setAuthError("That passcode is not correct.");
      return;
    }
    window.sessionStorage.setItem("popTeamCode", entry);
    setCode(entry);
  };

  const logout = () => {
    window.sessionStorage.removeItem("popTeamCode");
    setCode("");
    setInquiries(null);
  };

  if (!code) {
    return (
      <div className="team-shell team-gate" data-testid="team-login-screen">
        <form className="team-gate-card" onSubmit={login}>
          <BrandMark compact />
          <h1>Team room</h1>
          <p>Enter the private passcode to review enquiries, deposits, and bookings.</p>
          <div className="team-gate-input"><Lock size={15} /><input type="password" value={entry} onChange={(event) => setEntry(event.target.value)} placeholder="Team passcode" data-testid="team-passcode-input" autoFocus /></div>
          {authError && <span className="team-gate-error" data-testid="team-login-error">{authError}</span>}
          <button type="submit" className="button button-primary" data-testid="team-login-button">Enter the room <ArrowUpRight size={15} /></button>
        </form>
      </div>
    );
  }

  const visible = (inquiries || []).filter((item) => filter === "all" || item.status === filter);
  return (
    <div className="team-shell" data-testid="team-dashboard">
      <header className="team-header">
        <BrandMark compact />
        <div className="team-header-actions">
          <button type="button" className="text-button" onClick={() => load(code)} data-testid="team-refresh-button"><RefreshCw size={14} /> Refresh</button>
          <button type="button" className="text-button" onClick={logout} data-testid="team-logout-button"><LogOut size={14} /> Lock room</button>
        </div>
      </header>
      <div className="team-intro">
        <h1>Enquiry room</h1>
        <p>{inquiries ? `${inquiries.length} enquir${inquiries.length === 1 ? "y" : "ies"} on file` : "Loading…"}</p>
      </div>
      <div className="team-filters">
        <div className="view-switch">
          <button type="button" className={`status-chip${view === "list" ? " is-active" : ""}`} onClick={() => setView("list")} data-testid="team-view-list"><List size={13} /> List</button>
          <button type="button" className={`status-chip${view === "calendar" ? " is-active" : ""}`} onClick={() => setView("calendar")} data-testid="team-view-calendar"><CalendarDays size={13} /> Calendar</button>
          <button type="button" className={`status-chip${view === "testimonials" ? " is-active" : ""}`} onClick={() => setView("testimonials")} data-testid="team-view-testimonials"><Star size={13} /> Testimonials</button>
        </div>
        {view === "list" && ["all", ...STATUS_OPTIONS].map((option) => (
          <button type="button" key={option} className={`filter-button${filter === option ? " is-active" : ""}`} onClick={() => setFilter(option)} data-testid={`team-filter-${option}`}>{option === "all" ? "All" : STATUS_LABELS[option]}</button>
        ))}
      </div>
      {inquiries === null ? (
        <div className="team-empty"><LoaderCircle className="spin" size={18} /> Loading enquiries…</div>
      ) : view === "calendar" ? (
        <TeamCalendar inquiries={inquiries} />
      ) : view === "testimonials" ? (
        <TeamTestimonials code={code} />
      ) : visible.length === 0 ? (
        <div className="team-empty" data-testid="team-empty-state">No enquiries here yet.</div>
      ) : (
        <div className="inquiry-list">{visible.map((inquiry) => <InquiryCard key={inquiry.id} inquiry={inquiry} code={code} onUpdated={() => load(code)} />)}</div>
      )}
    </div>
  );
}
